import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  MAX_LABELS_PER_REQUEST,
  MAX_LABEL_COPIES,
  labelSheetsNeeded,
} from '@eombor/contracts';
import { Badge, Button, Dialog, Input, cn, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { printBlob } from '../../../shared/api/print.js';
import { t } from '../../../shared/i18n/index.js';
import { useLabelSize } from '../../warehouse/model/label-size.js';
import { LABEL_FILE_NAME, downloadBlob, labelApi } from '../../warehouse/api/label.api.js';

/**
 * Bitta bosiladigan partiya — oyna uni QAYERDAN kelganini bilmaydi.
 *
 * Kirim hujjati ham, mahsulot kartochkasi ham shu shaklda beradi: ikkovida
 * ham partiya, nomi va bir qator izoh bor, lekin manbalari butunlay
 * boshqacha (`ReceiptLineDto` va `StockPlaceDto`).
 */
export interface BatchLabelTarget {
  batchId: string;
  batchNo: string;
  name: string;
  /** Ikkinchi qator — katak kodi, miqdor va shunga o'xshash. */
  hint?: string;
}

export interface BatchLabelDialogProps {
  open: boolean;
  onClose: () => void;
  targets: readonly BatchLabelTarget[];
}

/**
 * MAHSULOT ETIKETKASI — qutiga yopishtiriladigan, partiya bo'yicha.
 *
 * O'LCHAM TANLOVI bu oynada YO'Q. Rulon omborga bir marta olinadi va
 * yiliga bir marta almashadi, mahsulot etiketkasi esa kuniga o'nlab
 * marta bosiladi — tanlov har bosishda javob berilishi kerakdek
 * ko'rinib, yo'lni to'sib turardi. Tanlangan o'lcham brauzerda
 * saqlanadi (`useLabelSize`) va uni javon yorliqlari oynasidan
 * o'zgartiriladi: o'sha yerda ro'yxat baribir ochiq turadi.
 *
 * ## MAHSULOT TERILADI, hammasi bosilmaydi
 *
 * Ro'yxat jimlikda TO'LIQ belgilangan holda ochiladi — odatiy hol shu:
 * kirim joylashtirildi, hammasiga etiketka kerak. Lekin belgini olib
 * tashlash mumkin va bu qog'oz masalasi: bitta satr uchun qayta bosish
 * kerak bo'lganda, butun hujjatni qaytadan bosish rulonning yarmini
 * yeb ketardi. Pastda esa NECHTA VARAQ ketishi turadi — bosishdan
 * oldin, keyin emas.
 *
 * Katak etiketkasidan (`LabelPrintDialog`) farqi nusxa sonida: katak bitta
 * va unga bitta yorliq kerak, mahsulot esa bir necha qutiga bo'lingan
 * bo'lishi mumkin. Shuning uchun har satrda alohida son turadi.
 *
 * Jimlikda BITTA: 120 dona bolt bitta qutida keladi. Skladchi sonni faqat
 * mol bo'lingan holatda ko'taradi — ya'ni odatiy kunda bu maydonga umuman
 * tegilmaydi.
 */
export const BatchLabelDialog = ({ open, onClose, targets }: BatchLabelDialogProps) => {
  const toast = useToast();
  const label = useLabelSize();
  const [copies, setCopies] = useState<Record<string, number>>({});

  /* Belgilanganlar ro'yxati KALIT bo'yicha tiklanadi: `targets` har
     chizishda yangi massiv bo'lib kelishi mumkin va uning o'ziga
     bog'lansa, belgilash har renderda nolga qaytardi. */
  const key = targets.map((target) => target.batchId).join(',');
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    setPicked(new Set(key === '' ? [] : key.split(',')));
  }, [key, open]);

  const items = useMemo(
    () =>
      targets
        .filter((target) => picked.has(target.batchId))
        .map((target) => ({
          batchId: target.batchId,
          copies: copies[target.batchId] ?? 1,
        })),
    [targets, picked, copies],
  );

  const total = items.reduce((sum, item) => sum + item.copies, 0);
  const tooMany = total > MAX_LABELS_PER_REQUEST;
  const empty = targets.length === 0;
  const nonePicked = !empty && picked.size === 0;

  /* Nechta VARAQ ketishi — bosishdan oldin. Yarimlangan rulonda bitta
     qog'ozga ikkita etiketka tushadi va farq aynan shu yerda ko'rinadi:
     "12 ta etiketka · ≈ 6 varaq". */
  const sheets = labelSheetsNeeded(total, label.preset, label.sizeInput().size);

  const print = useMutation({
    mutationFn: (use: (blob: Blob) => void) =>
      labelApi
        .printBatches({ items, preset: label.preset, ...label.sizeInput() })
        .then((blob) => {
          use(blob);
        }),
    onSuccess: () => {
      toast.show(t('stock.batchLabelDone'), 'success');
      onClose();
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('stock.batchLabelFailed'),
        'danger',
      );
    },
  });

  const blocked = tooMany || empty || nonePicked || label.sizeMissing;

  const toggle = (batchId: string): void => {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  };

  const allPicked = !empty && picked.size === targets.length;

  /**
   * Bo'sh maydon — `1` emas, BO'SH.
   *
   * Sonni o'zgartirmoqchi bo'lgan odam avval borini o'chiradi; o'sha
   * lahzada maydon `1` ga sakrab tursa, u yozgan raqam oldiga qo'shilib
   * `15` bo'lib ketardi. Bo'sh qiymat so'rovda `1` deb hisoblanadi.
   */
  const changeCopies = (batchId: string, raw: string): void => {
    if (raw === '') {
      setCopies((current) => {
        const next = { ...current };
        delete next[batchId];
        return next;
      });
      return;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    const clamped = Math.min(MAX_LABEL_COPIES, Math.max(1, Math.round(value)));
    setCopies((current) => ({ ...current, [batchId]: clamped }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('stock.batchLabelTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('stock.cancel')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => print.mutate((blob) => downloadBlob(blob, LABEL_FILE_NAME))}
            loading={print.isPending}
            disabled={blocked}
          >
            {t('warehouse.labelDownload')}
          </Button>
          <Button onClick={() => print.mutate(printBlob)} loading={print.isPending} disabled={blocked}>
            {t('warehouse.labelSubmit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {empty ? null : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-text">
              <input
                type="checkbox"
                className="size-4 accent-accent"
                checked={allPicked}
                onChange={() =>
                  setPicked(
                    allPicked ? new Set() : new Set(targets.map((target) => target.batchId)),
                  )
                }
              />
              {t('stock.batchLabelAll')}
            </label>

            {/* Etiketka soni va QOG'OZ soni yonma-yon: qaysi varaq turi
                tanlanganini bilmagan odam ham farqni shu yerda ko'radi. */}
            <p className="text-sm text-text-muted">
              {t('stock.batchLabelCount', { count: total })}
              {sheets === null ? '' : ` · ${t('stock.batchLabelSheets', { count: sheets })}`}
            </p>
          </div>
        )}

        <ul className="flex flex-col gap-2">
          {targets.map((target) => {
            const on = picked.has(target.batchId);
            return (
              <li
                key={target.batchId}
                className={cn(
                  'border-border flex items-center gap-3 rounded-[var(--radius-sm)] border p-2',
                  'transition-colors duration-[var(--motion-fast)]',
                  on ? 'bg-surface' : 'bg-surface-2',
                )}
              >
                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    className="size-4 shrink-0 accent-accent"
                    checked={on}
                    onChange={() => toggle(target.batchId)}
                    aria-label={target.name}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className={cn(
                        'truncate text-sm font-medium',
                        on ? 'text-text' : 'text-text-faint',
                      )}
                    >
                      {target.name}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-text-faint">
                      <Badge tone="neutral" mono>
                        {target.batchNo}
                      </Badge>
                      {target.hint === undefined ? null : (
                        <span className="truncate">{target.hint}</span>
                      )}
                    </span>
                  </span>
                </label>

                {/* Yorliq YOZILMAYDI, `aria-label` bilan beriladi: har satrda
                    takrorlangan "Nusxa" so'zi ro'yxatni shovqinga to'ldirardi,
                    ekran o'quvchiga esa u baribir yetib boradi. */}
                <Input
                  aria-label={t('stock.batchLabelCopies')}
                  type="number"
                  inputMode="numeric"
                  numeric
                  min={1}
                  max={MAX_LABEL_COPIES}
                  className="w-20 shrink-0"
                  disabled={!on}
                  value={String(copies[target.batchId] ?? '')}
                  placeholder="1"
                  onChange={(event) => changeCopies(target.batchId, event.target.value)}
                />
              </li>
            );
          })}
        </ul>

        <p className="text-xs text-text-faint">{t('stock.batchLabelHint')}</p>

        {tooMany ? (
          <p role="alert" className="text-sm text-danger">
            {t('warehouse.labelTooMany', { max: MAX_LABELS_PER_REQUEST })}
          </p>
        ) : null}

        {nonePicked ? (
          <p role="alert" className="text-sm text-warn">
            {t('stock.batchLabelNonePicked')}
          </p>
        ) : null}

        {empty ? (
          <p role="alert" className="text-sm text-warn">
            {t('stock.batchLabelEmpty')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
};
